import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Icon,
	IconButton,
	MenuItem,
	Typography,
	Checkbox,
	FormControlLabel,
	Card,
	CardHeader,
	CardContent,
	Box,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Select,
	FormControl,
	InputLabel,
	TextField,
	CircularProgress
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { useForm } from '@fuse/hooks';
import { SelectFormsy } from '@fuse';
import Formsy from 'formsy-react';

import * as UserActions from '../../auth/store/actions/';
import * as ActionsDialog from 'app/store/actions';
import { getLdapServers } from 'app/main/settings/store/actions/ldap.actions';
import { getOpenIdConfigs } from 'app/main/settings/store/actions/oidc.actions';
import {
  addLdapCollaborator,
  removeLdapCollaborator,
  addOpenidcCollaborator,
  removeOpenidcCollaborator
} from 'app/main/cluster/store/actions/cluster.actions';
import { USER_AUTH_TYPE, USER_ROLE } from 'app/main/util/enums';

const useStyles = makeStyles((theme) => ({
  table: {
    '& th': {
      padding: '4px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
    },
    '& td': {
      paddingBottom: '2px'
    }
  },
  card: {
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)',
    borderRadius: '8px',
    overflow: 'visible'
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
    backgroundColor: '#f7f9fc'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f2147'
  },
  dialogContent: {
    padding: '0px',
    margin: '10px 0px 0px 20px',
    width: '95%',
    maxWidth: '100%',
    overflow: 'auto',
    maxHeight: 'calc(100vh - 120px)', // Adjust based on header height
    flex: 1 // Take remaining space
  },
  addSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  addRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%'
  },
  selectWrapper: {
    flex: 1,
    minWidth: '250px'
  },
  typeSelector: {
    minWidth: '150px',
    maxWidth: '180px'
  },
  mainContainer: {
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden' // Hide the global scrollbar
  },
  modalHeader: {
    background: 'linear-gradient(to right, #1976d2 0%, #3b82f6 100%)', // Same blue gradient as AWS form
    color: 'white'
  },
  nameColumn: {
    width: '35%',
    minWidth: '120px'
  },
  typeColumn: {
    width: '25%',
    minWidth: '100px'
  },
  sudoColumn: {
    width: '25%',
    minWidth: '90px',
    textAlign: 'center'
  },
  actionsColumn: {
    width: '15%',
    minWidth: '60px',
    textAlign: 'right'
  },
  stickyTableHead: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 10
  },
  tableContainer: {
    maxHeight: '400px',
    overflow: 'auto'
  }
}));

const Collaborators = props => {
  const dispatch = useDispatch();
  const classes = useStyles();

  // redux selectors
  const cluster = useSelector(({ clusters }) => clusters.cluster.selected);
  const credential = useSelector(({ credentials }) => credentials.credential.selected);
  const stack = useSelector(({ stacks }) => stacks.stack.selected);
  const username = useSelector(({ auth }) => auth.user.username);
  const allUsers = useSelector(({ auth }) => auth.user.users);
  const rawLdap = useSelector(({ settings }) => settings.settings.ldapServers || []);
  const rawOidc = useSelector(({ settings }) => settings.settings.oidcConfigs || []);

  // component state
  const [data, setData] = useState(props.resource.data);
  const [users, setUsers] = useState([]);
  const [ldapServers, setLdapServers] = useState([]);
  const [openidcConfigs, setOpenidcConfigs] = useState([]);
  const [selectedLdapServer, setSelectedLdapServer] = useState('');
  const [selectedOpenidcConfig, setSelectedOpenidcConfig] = useState('');
  const { form, handleChange, resetForm, setForm } = useForm({ collaborator: '' });
  const [id] = useState(props.resource.data.id);
  const [type] = useState(props.resource.type);
  const [grantSudo, setGrantSudo] = useState(false);
  const [collaboratorType, setCollaboratorType] = useState('local'); // 'local', 'ldap', 'oidc'
  const [deletingUsers, setDeletingUsers] = useState(new Set()); // Track which users are being deleted
  const [isAdding, setIsAdding] = useState(false); // Track if any add operation is in progress
  const [selectedUser, setSelectedUser] = useState(null); // Track selected user object for admin check

  useEffect(() => {
    dispatch(UserActions.getUsers());
    if (type === 'cluster') {
      dispatch(getLdapServers());
      dispatch(getOpenIdConfigs());
    }
  }, [dispatch, type]);

  useEffect(() => {
    switch (type) {
      case 'cluster':
        // Sync with Redux selected cluster to get latest updates after collaborator operations
        if (cluster && cluster.id === id) {
          setData(cluster);
        } else {
          setData(props.resource.data);
        }
        break;
      case 'stack':
        stack && setData(stack);
        break;
      case 'credential':
        credential && setData(credential);
        break;
      default:
        break;
    }
  }, [props.resource.data, credential, stack, type, cluster, id]);

  useEffect(() => {
    if (allUsers.length > 0 && data) {
      const filtered = allUsers.filter(u =>
        u.authType === USER_AUTH_TYPE.LOCAL &&
        !data.user.includes(u.name) &&
        (type === 'cluster' || u.admin)
      );
      setUsers(filtered);
    }
  }, [allUsers, data, type]);

  useEffect(() => {
    if (rawLdap.length > 0 && data) {
      const filtered = rawLdap.filter(srv =>
        !data.sharedLdapServers?.some(shared => shared.id === srv.id)
      );
      setLdapServers(filtered);
    }
  }, [rawLdap, data]);

  useEffect(() => {
    if (rawOidc.length > 0 && data) {
      const filtered = rawOidc.filter(cfg =>
        !data.sharedOpenidcConfigs?.some(shared => shared.id === cfg.id)
      );
      setOpenidcConfigs(filtered);
    }
  }, [rawOidc, data]);

  const addCollaborator = async () => {
    setIsAdding(true);
    try {
      // Check if selected user is admin and always grant sudo to admins
      const isAdmin = selectedUser?.role?.includes(USER_ROLE.ADMIN);
      const shouldGrantSudo = grantSudo || isAdmin;

      await dispatch(props.actions.put({ id, user: form.collaborator, type: 'share', hasSudo: shouldGrantSudo }));

      // Update local state immediately for better UX
      const userToAdd = users.find(u => u.id === form.collaborator);
      if (userToAdd) {
        setData(prevData => {
          const updatedData = { ...prevData };

          // Add user to the user array if not already present
          if (!updatedData.user.includes(userToAdd.name)) {
            updatedData.user = [...updatedData.user, userToAdd.name];
          }

          // Add sudo permission if granted or user is admin
          if (shouldGrantSudo) {
            updatedData.userSudoPermissions = {
              ...updatedData.userSudoPermissions,
              [userToAdd.name]: true
            };
          }

          return updatedData;
        });
      }

      resetForm();
      setGrantSudo(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error adding collaborator:', error);
    } finally {
      setIsAdding(false);
    }
  };
  const deleteCollaborator = async (user) => {
    try {
      // Add user to deleting set to show spinner
      setDeletingUsers(prev => new Set([...prev, user]));
      
      // Dispatch the Redux action and wait for it to complete
      await dispatch(props.actions.put({ id, user, type: 'unshare' }));
      
      // Remove user from deleting set after successful deletion
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(user);
        return newSet;
      });
      
      // Don't update local state manually - let Redux handle it
      // The editCollaborators action already updates the store with the latest data
      
    } catch (error) {
      console.error('Error removing collaborator:', error);
      
      // Remove user from deleting set on error
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(user);
        return newSet;
      });
      
      // Show error notification
      dispatch(ActionsDialog.showMessage({
        message: `Failed to remove collaborator: ${error.message || 'Unknown error'}`,
        variant: 'error'
      }));
    }
  };
  const deleteLdapServer = async serverId => {
    setDeletingUsers(prev => new Set(prev).add(serverId));
    try {
      await dispatch(removeLdapCollaborator({ id, serverId }));
      // Update local state to reflect the removal
      setData(prevData => ({
        ...prevData,
        sharedLdapServers: prevData.sharedLdapServers?.filter(server => server.id !== serverId) || []
      }));
    } catch (error) {
      console.error('Failed to delete LDAP server:', error);
    } finally {
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(serverId);
        return newSet;
      });
    }
  };
  const addLDAPCollaborator = async () => {
    if (!selectedLdapServer) return;
    setIsAdding(true);
    try {
      await dispatch(addLdapCollaborator({ id, serverId: selectedLdapServer }));
      setSelectedLdapServer('');
    } catch (error) {
      console.error('Error adding LDAP collaborator:', error);
    } finally {
      setIsAdding(false);
    }
  };
  const deleteOpenidcCollaborator = async openidcConfigId => {
    setDeletingUsers(prev => new Set(prev).add(openidcConfigId));
    try {
      await dispatch(removeOpenidcCollaborator({ id, openidcConfigId }));
      // Update local state to reflect the removal
      setData(prevData => ({
        ...prevData,
        sharedOpenidcConfigs: prevData.sharedOpenidcConfigs?.filter(config => config.id !== openidcConfigId) || []
      }));
    } catch (error) {
      console.error('Failed to delete OIDC config:', error);
    } finally {
      setDeletingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(openidcConfigId);
        return newSet;
      });
    }
  };
  const addOpenidcCollaboratorToCluster = async () => {
    if (!selectedOpenidcConfig) return;
    setIsAdding(true);
    try {
      await dispatch(addOpenidcCollaborator({ id, openidcConfigId: selectedOpenidcConfig }));
      setSelectedOpenidcConfig('');
    } catch (error) {
      console.error('Error adding OIDC collaborator:', error);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className={classes.mainContainer}>
      {/* Fixed Header */}
      <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')} style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <div className="flex items-center justify-between w-full">
          <Typography variant="h6" style={{ color: 'white', fontWeight: 600 }}>
            {`Collaborators: ${data.name}`}
          </Typography>
          <Button
            className="whitespace-no-wrap"
            id="collaborators_close-btn"
            variant="contained"
            onClick={() => dispatch(ActionsDialog.closeDialog())}
          >
            CLOSE
          </Button>
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {data ? (
          <div>
            {/* Current Collaborators Card */}
            <Card className={classes.card}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center">
                    <Icon style={{marginRight: '8px', color: '#2196F3'}}>people</Icon>
                    <Typography className={classes.sectionTitle}>Current Collaborators</Typography>
                  </Box>
                }
                className={classes.cardHeader}
              />
              <CardContent>
                <div className={classes.tableContainer}>
                  <Table stickyHeader>
                    <TableHead className={classes.stickyTableHead}>
                      <TableRow>
                        <TableCell className={classes.nameColumn}><strong>Name</strong></TableCell>
                        <TableCell className={classes.typeColumn}><strong>Type</strong></TableCell>
                        {type === 'cluster' && <TableCell className={classes.sudoColumn}><strong>Sudo Access</strong></TableCell>}
                        <TableCell className={classes.actionsColumn} align="right"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {/* Local Users */}
                    {data.user
                      .filter(user => {
                        const userObj = allUsers.find(u => u.name === user);
                        return (
                          !userObj ||
                          (userObj.authType !== USER_AUTH_TYPE.LDAP &&
                            userObj.authType !== USER_AUTH_TYPE.OPENID_CONNECT)
                        );
                      })
                      .map((user, i) => {
                        const hasSudo = data.userSudoPermissions && data.userSudoPermissions[user];
                        const userObj = allUsers.find(u => u.name === user);
                        const isAdmin = userObj?.role?.includes(USER_ROLE.ADMIN);
                        const displayName = userObj ? `${userObj.firstName} ${userObj.lastName} (${userObj.name})` : `${user.charAt(0).toUpperCase()}${user.slice(1)}`;
                        return (
                          <TableRow id={`user-${i}`} key={i}>
                            <TableCell className={classes.nameColumn}>{displayName}</TableCell>
                            <TableCell className={classes.typeColumn}>Local User</TableCell>
                            {type === 'cluster' && (
                              <TableCell className={classes.sudoColumn}>
                                <Checkbox
                                  checked={hasSudo || isAdmin || false}
                                  disabled={true}
                                  title={isAdmin ? "Admin users automatically have sudo access" : "Sudo permissions can only be set when adding a collaborator"}
                                  onChange={async (e) => {
                                    const hasPermission = e.target.checked;
                                    try {
                                      await dispatch(props.actions.put({
                                        id,
                                        user,
                                        type: 'update-sudo',
                                        hasSudo: hasPermission
                                      }));
                                      // Update local state to reflect the change immediately
                                      setData(prevData => ({
                                        ...prevData,
                                        userSudoPermissions: {
                                          ...prevData.userSudoPermissions,
                                          [user]: hasPermission
                                        }
                                      }));
                                    } catch (error) {
                                      console.error('Failed to update sudo permission:', error);
                                    }
                                  }}
                                  color="primary"
                                />
                              </TableCell>
                            )}
                            <TableCell className={classes.actionsColumn} align="right">
                              <IconButton
                                disabled={
                                  user === data.user[0] ||
                                  deletingUsers.has(user)
                                }
                                id={`${user}-del`}
                                className="cursor-pointer p-0"
                                onClick={() => deleteCollaborator(user)}
                              >
                                {deletingUsers.has(user) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Icon>delete</Icon>
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    
                    {/* LDAP Users */}
                    {data.user
                      .filter(user => {
                        const userObj = allUsers.find(u => u.name === user);
                        return userObj && userObj.authType === USER_AUTH_TYPE.LDAP;
                      })
                      .map((user, i) => {
                        const hasSudo = data.userSudoPermissions && data.userSudoPermissions[user];
                        const userObj = allUsers.find(u => u.name === user);
                        const isAdmin = userObj?.role?.includes(USER_ROLE.ADMIN);
                        const displayName = userObj ? `${userObj.firstName} ${userObj.lastName} (${userObj.name})` : `${user.charAt(0).toUpperCase()}${user.slice(1)}`;
                        return (
                          <TableRow id={`ldap-user-${i}`} key={`ldap-user-${i}`}>
                            <TableCell className={classes.nameColumn}>{displayName}</TableCell>
                            <TableCell className={classes.typeColumn}>LDAP User</TableCell>
                            {type === 'cluster' && (
                              <TableCell className={classes.sudoColumn}>
                                <Checkbox
                                  checked={hasSudo || isAdmin || false}
                                  disabled={true}
                                  title={isAdmin ? "Admin users automatically have sudo access" : "Sudo permissions can only be set when adding a collaborator"}
                                  onChange={async (e) => {
                                    const hasPermission = e.target.checked;
                                    try {
                                      await dispatch(props.actions.put({
                                        id,
                                        user,
                                        type: 'update-sudo',
                                        hasSudo: hasPermission
                                      }));
                                      setData(prevData => ({
                                        ...prevData,
                                        userSudoPermissions: {
                                          ...prevData.userSudoPermissions,
                                          [user]: hasPermission
                                        }
                                      }));
                                    } catch (error) {
                                      console.error('Failed to update sudo permission:', error);
                                    }
                                  }}
                                  color="primary"
                                />
                              </TableCell>
                            )}
                            <TableCell className={classes.actionsColumn} align="right">
                              <IconButton
                                id={`${user}-del`}
                                className="cursor-pointer p-0"
                                onClick={() => deleteCollaborator(user)}
                              >
                                <Icon>delete</Icon>
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    
                    {/* OIDC Users */}
                    {data.user
                      .filter(user => {
                        const userObj = allUsers.find(u => u.name === user);
                        return userObj && userObj.authType === USER_AUTH_TYPE.OPENID_CONNECT;
                      })
                      .map((user, i) => {
                        const hasSudo = data.userSudoPermissions && data.userSudoPermissions[user];
                        const userObj = allUsers.find(u => u.name === user);
                        const isAdmin = userObj?.role?.includes(USER_ROLE.ADMIN);
                        const displayName = userObj ? `${userObj.firstName} ${userObj.lastName} (${userObj.name})` : `${user.charAt(0).toUpperCase()}${user.slice(1)}`;
                        return (
                          <TableRow id={`oidc-user-${i}`} key={`oidc-user-${i}`}>
                            <TableCell className={classes.nameColumn}>{displayName}</TableCell>
                            <TableCell className={classes.typeColumn}>OIDC User</TableCell>
                            {type === 'cluster' && (
                              <TableCell className={classes.sudoColumn}>
                                <Checkbox
                                  checked={hasSudo || isAdmin || false}
                                  disabled={true}
                                  title={isAdmin ? "Admin users automatically have sudo access" : "Sudo permissions can only be set when adding a collaborator"}
                                  onChange={async (e) => {
                                    const hasPermission = e.target.checked;
                                    try {
                                      await dispatch(props.actions.put({
                                        id,
                                        user,
                                        type: 'update-sudo',
                                        hasSudo: hasPermission
                                      }));
                                      setData(prevData => ({
                                        ...prevData,
                                        userSudoPermissions: {
                                          ...prevData.userSudoPermissions,
                                          [user]: hasPermission
                                        }
                                      }));
                                    } catch (error) {
                                      console.error('Failed to update sudo permission:', error);
                                    }
                                  }}
                                  color="primary"
                                />
                              </TableCell>
                            )}
                            <TableCell className={classes.actionsColumn} align="right">
                              <IconButton
                                disabled={deletingUsers.has(user)}
                                id={`${user}-del`}
                                className="cursor-pointer p-0"
                                onClick={() => deleteCollaborator(user)}
                              >
                                {deletingUsers.has(user) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Icon>delete</Icon>
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    
                    {/* LDAP Servers */}
                    {data.sharedLdapServers?.map((server, i) => (
                      <TableRow id={`ldap-${i}`} key={`ldap-${i}`}>
                        <TableCell className={classes.nameColumn}>{server.label}</TableCell>
                        <TableCell className={classes.typeColumn}>LDAP Server</TableCell>
                        {type === 'cluster' && <TableCell className={classes.sudoColumn}>-</TableCell>}
                        <TableCell className={classes.actionsColumn} align="right">
                          <IconButton
                            disabled={deletingUsers.has(server.id)}
                            id={`${server.id}-del`}
                            className="cursor-pointer p-0"
                            onClick={() => deleteLdapServer(server.id)}
                          >
                            {deletingUsers.has(server.id) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Icon>delete</Icon>
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* OIDC Configs */}
                    {data.sharedOpenidcConfigs?.map((config, i) => (
                      <TableRow id={`openidc-${i}`} key={`openidc-${i}`}>
                        <TableCell className={classes.nameColumn}>{config.label}</TableCell>
                        <TableCell className={classes.typeColumn}>OIDC Config</TableCell>
                        {type === 'cluster' && <TableCell className={classes.sudoColumn}>-</TableCell>}
                        <TableCell className={classes.actionsColumn} align="right">
                          <IconButton
                            disabled={deletingUsers.has(config.id)}
                            id={`${config.id}-del`}
                            className="cursor-pointer p-0"
                            onClick={() => deleteOpenidcCollaborator(config.id)}
                          >
                            {deletingUsers.has(config.id) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Icon>delete</Icon>
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>

            {/* Add Collaborators Card - Consolidated */}
            <Card className={classes.card}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center">
                    <Icon style={{marginRight: '8px', color: '#4CAF50'}}>person_add</Icon>
                    <Typography className={classes.sectionTitle}>Add Collaborators</Typography>
                  </Box>
                }
                className={classes.cardHeader}
              />
              <CardContent>
                <div className={classes.addSection}>
                  {/* First Row: Type selector and User/Server selector with Add button */}
                  <div className={classes.addRow}>
                    <div className={classes.typeSelector}>
                      <Formsy>
                        <SelectFormsy
                          id="collaborators_type_selector"
                          name="collaboratorType"
                          label="Type"
                          variant="outlined"
                          value={collaboratorType}
                          onChange={(e) => setCollaboratorType(e.target.value)}
                          fullWidth
                          required
                        >
                          <MenuItem value="local">Local Users</MenuItem>
                          {type === 'cluster' && ldapServers.length > 0 && (
                            <MenuItem value="ldap">LDAP Servers</MenuItem>
                          )}
                          {type === 'cluster' && openidcConfigs.length > 0 && (
                            <MenuItem value="oidc">OIDC Configs</MenuItem>
                          )}
                        </SelectFormsy>
                      </Formsy>
                    </div>

                    {/* Dynamic Content Based on Type */}
                    {collaboratorType === 'local' && (
                      <>
                        <div className={classes.selectWrapper}>
                          {users.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                              {allUsers.length === 0 ? 'Loading users…' : 'No users to add...'}
                            </Typography>
                          ) : (
                            <Autocomplete
                              id="collaborators_collaborator"
                              className={classes.selectWrapper}
                              options={users}
                              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.name})`}
                              renderInput={(params) =>
                                <TextField
                                  {...params}
                                  label="Select User"
                                  placeholder="Search and select a user to add as collaborator"
                                  variant="outlined"
                                  fullWidth
                                  required
                                />
                              }
                              onChange={(event, newValue) => {
                                setForm({
                                  ...form,
                                  collaborator: newValue ? newValue.id : ''
                                });
                                setSelectedUser(newValue);
                                // Auto-check sudo for admin users
                                if (newValue?.role?.includes(USER_ROLE.ADMIN)) {
                                  setGrantSudo(true);
                                }
                              }}
                              value={users.find(u => u.id === form.collaborator) || null}
                            />
                          )}
                        </div>
                        {users.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            id="collaborators_add-collaborator-btn"
                            disabled={!form.collaborator || isAdding}
                            onClick={addCollaborator}
                            startIcon={isAdding ? <CircularProgress size={16} /> : <Icon>add</Icon>}
                          >
                            {isAdding ? 'ADDING...' : 'ADD'}
                          </Button>
                        )}
                      </>
                    )}

                    {collaboratorType === 'ldap' && type === 'cluster' && (
                      <>
                        <div className={classes.selectWrapper}>
                          {ldapServers.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                              {rawLdap.length === 0
                                ? 'Loading LDAP servers…'
                                : 'No LDAP servers to add...'}
                            </Typography>
                          ) : (
                            <Formsy>
                              <SelectFormsy
                                id="collaborators_ldap"
                                name="ldapServer"
                                label="LDAP Server"
                                variant="outlined"
                                value={selectedLdapServer}
                                onChange={e => setSelectedLdapServer(e.target.value)}
                                fullWidth
                                required
                                displayEmpty
                                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                              >
                                <MenuItem value="" disabled>
                                  Select User
                                </MenuItem>
                                {ldapServers.map((srv, i) => (
                                  <MenuItem id={srv.id} key={i} value={srv.id}>
                                    {srv.label}
                                  </MenuItem>
                                ))}
                              </SelectFormsy>
                            </Formsy>
                          )}
                        </div>
                        {ldapServers.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            id="collaborators_add-ldap-collaborator-btn"
                            disabled={!selectedLdapServer || isAdding}
                            onClick={addLDAPCollaborator}
                            startIcon={isAdding ? <CircularProgress size={16} /> : <Icon>add</Icon>}
                          >
                            {isAdding ? 'ADDING...' : 'ADD'}
                          </Button>
                        )}
                      </>
                    )}

                    {collaboratorType === 'oidc' && type === 'cluster' && (
                      <>
                        <div className={classes.selectWrapper}>
                          {openidcConfigs.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                              {rawOidc.length === 0
                                ? 'Loading OIDC configs…'
                                : 'No OIDC configs to add...'}
                            </Typography>
                          ) : (
                            <Formsy>
                              <SelectFormsy
                                id="collaborators_openidc"
                                name="openidcConfig"
                                label="OIDC Config"
                                variant="outlined"
                                value={selectedOpenidcConfig}
                                onChange={e => setSelectedOpenidcConfig(e.target.value)}
                                fullWidth
                                required
                                displayEmpty
                                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                              >
                                <MenuItem value="" disabled>
                                  Select User
                                </MenuItem>
                                {openidcConfigs.map((cfg, i) => (
                                  <MenuItem id={cfg.id} key={i} value={cfg.id}>
                                    {cfg.label}
                                  </MenuItem>
                                ))}
                              </SelectFormsy>
                            </Formsy>
                          )}
                        </div>
                        {openidcConfigs.length > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            id="collaborators_add-oidc-collaborator-btn"
                            disabled={!selectedOpenidcConfig || isAdding}
                            onClick={addOpenidcCollaboratorToCluster}
                            startIcon={isAdding ? <CircularProgress size={16} /> : <Icon>add</Icon>}
                          >
                            {isAdding ? 'ADDING...' : 'ADD'}
                          </Button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Second Row: Grant Sudo Access - Only show for local users, not for credentials */}
                  {collaboratorType === 'local' && users.length > 0 && type === 'cluster' && (
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={grantSudo}
                            onChange={(e) => setGrantSudo(e.target.checked)}
                            disabled={selectedUser?.role?.includes(USER_ROLE.ADMIN)}
                            color="primary"
                          />
                        }
                        label="Grant Sudo Access"
                      />
                      {selectedUser?.role?.includes(USER_ROLE.ADMIN) && (
                        <Typography variant="caption" color="textSecondary" style={{ marginLeft: '32px', display: 'block', marginTop: '-8px' }}>
                          This user is an admin and automatically has sudo access
                        </Typography>
                      )}
                    </Box>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Typography variant="h6" align="center" style={{ padding: '40px' }}>
            ...Loading Collaborators
          </Typography>
        )}
      </DialogContent>
    </div>
  );
};


export default Collaborators;
